// @ts-check
/**
 * FF Recently Viewed - Custom element to track and display recently viewed products
 *
 * Features:
 * - Tracks viewed products in localStorage
 * - Loads product cards via AJAX using section rendering API
 * - Integrates with ff-slider for carousel display
 * - Auto-hides when no products to show
 */

if (!customElements.get('ff-recently-viewed')) {
  class FfRecentlyViewed extends HTMLElement {
    static STORAGE_KEY = 'ff-recently-viewed';
    static MAX_STORED = 20;

    constructor() {
      super();

      /** @type {HTMLElement | null} */
      this.ffSlider = null;

      /** @type {object | null} */
      this.splideInstance = null;

      /** @type {string} */
      this.sectionId = this.dataset.sectionId || '';

      /** @type {string} */
      this.currentHandle = this.dataset.currentProduct || '';

      /** @type {number} */
      this.maxProducts = parseInt(this.dataset.maxProducts || '8', 10);

      /** @type {string} */
      this.cardSelector = this.dataset.cardSelector || '.ff-rv-card';
    }

    connectedCallback() {
      this.ffSlider = this.querySelector('ff-slider');

      this.saveCurrentProduct();
      this.initWhenReady();
    }

    initWhenReady() {
      // @ts-ignore - splide is added by ff-slider custom element
      if (this.ffSlider && this.ffSlider.splide) {
        // @ts-ignore
        this.splideInstance = this.ffSlider.splide;
        this.loadRecentlyViewed();
      } else {
        setTimeout(() => this.initWhenReady(), 100);
      }
    }

    /**
     * Get stored product handles from localStorage
     * @returns {string[]}
     */
    getStoredHandles() {
      try {
        const stored = localStorage.getItem(FfRecentlyViewed.STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        console.warn('FfRecentlyViewed: Error reading localStorage', e);
        return [];
      }
    }

    /**
     * Save handles to localStorage
     * @param {string[]} handles
     */
    saveHandles(handles) {
      try {
        localStorage.setItem(FfRecentlyViewed.STORAGE_KEY, JSON.stringify(handles));
      } catch (e) {
        console.warn('FfRecentlyViewed: Error saving to localStorage', e);
      }
    }

    saveCurrentProduct() {
      if (!this.currentHandle) return;

      let handles = this.getStoredHandles();

      handles = handles.filter((h) => h !== this.currentHandle);
      handles.unshift(this.currentHandle);
      handles = handles.slice(0, FfRecentlyViewed.MAX_STORED);

      this.saveHandles(handles);
    }

    async loadRecentlyViewed() {
      const handles = this.getStoredHandles()
        .filter((h) => h !== this.currentHandle)
        .slice(0, this.maxProducts);

      if (handles.length === 0) {
        this.hide();
        return;
      }

      const cards = await this.fetchProductCards(handles);

      if (cards.length === 0) {
        this.hide();
        return;
      }

      this.renderCards(cards);
    }

    /**
     * Fetch product cards via section rendering API
     * @param {string[]} handles
     * @returns {Promise<string[]>}
     */
    async fetchProductCards(handles) {
      const cards = [];
      const fetchPromises = handles.map((handle) => this.fetchSingleProduct(handle));
      const results = await Promise.allSettled(fetchPromises);

      for (const result of results) {
        if (result.status === 'fulfilled' && result.value) {
          cards.push(result.value);
        }
      }

      return cards;
    }

    /**
     * Fetch a single product page and extract the card HTML
     * @param {string} handle
     * @returns {Promise<string | null>}
     */
    async fetchSingleProduct(handle) {
      try {
        const url = `${window.Shopify.routes.root}products/${handle}?section_id=${this.sectionId}`;
        const response = await fetch(url);

        if (!response.ok) {
          console.warn(`FfRecentlyViewed: Failed to fetch ${handle}`, response.status);
          return null;
        }

        const html = await response.text();
        return this.extractProductCard(html);
      } catch (e) {
        console.warn(`FfRecentlyViewed: Error fetching ${handle}`, e);
        return null;
      }
    }

    /**
     * Extract product card HTML from the fetched section
     * @param {string} html
     * @returns {string | null}
     */
    extractProductCard(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const template = doc.querySelector('template[data-product-card-template]');
      if (template) {
        return template.innerHTML;
      }

      const card = doc.querySelector(this.cardSelector);
      if (card) {
        return `<li class="splide__slide">${card.outerHTML}</li>`;
      }

      return null;
    }

    /**
     * Render product cards in the slider
     * @param {string[]} cards
     */
    renderCards(cards) {
      if (!this.ffSlider || !this.splideInstance) return;

      const list = this.ffSlider.querySelector('.splide__list');
      if (!list) return;

      list.innerHTML = cards.join('');

      this.splideInstance.go(0);
      this.splideInstance.refresh();

      this.show();
    }

    hide() {
      this.classList.add('ff-d-none');
    }

    show() {
      this.classList.replace('ff-d-none', 'ff-d-block');
    }
  }

  customElements.define('ff-recently-viewed', FfRecentlyViewed);
}
